import { useEffect, useState } from "react";
import styles from "./SettingsMenu.module.scss";
import ToggleInput from "./inputs/ToggleInput";
import TermsOfUse from "../notes/TermsOfUse";
import Disclaimer from "../notes/Disclaimer";
import { useDispatch, useSelector } from "react-redux";
import { setSettings } from "../../store/variables/varActions";
import { genericTranslate, getMobileAppSettings } from "../../lib/funcs";
import { RootState } from "../../store/store";
import { useLanguage } from "../../lib/LanguageContext";
const defaultLanguageData = require("../../lib/lang/en.json");

function SettingsMenu() {
  const dispatch = useDispatch();
  const settings = useSelector((state: RootState) => state.variables.settings);
  const [open, setOpen] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const { languageData, setLanguageData, setLanguageCode } = useLanguage();
  const supportedLanguages: { code: string; label: string }[] = [
    { code: "en", label: "English" },
    { code: "ar", label: "العربية" },
    { code: "fa", label: "فارسی" },
    { code: "es", label: "Español" },
    { code: "cs", label: "Čeština" },
    { code: "de", label: "Deutsch" },
    { code: "el", label: "Ελληνικά" },
    { code: "fr", label: "Français" },
    { code: "hi", label: "हिन्दी" },
    { code: "id", label: "Bahasa Indonesia" },
    { code: "it", label: "Italiano" },
    { code: "ja", label: "日本語" },
    { code: "ko", label: "한국어" },
    { code: "pt", label: "Português" },
    { code: "ru", label: "Русский" },
    { code: "tr", label: "Türkçe" },
    { code: "vi", label: "Tiếng Việt" },
    { code: "zh", label: "中文" },
  ];

  const urlParams = new URLSearchParams(window.location.search);
  const getInitialLang = () => {
    const qp = (urlParams.get("lang") || "").toLowerCase();
    if (qp) return qp;
    try {
      const saved = localStorage.getItem("lang") || "";
      if (saved) return saved.toLowerCase();
    } catch (e) {}
    return "en";
  };
  const [selectedLanguage, setSelectedLanguage] = useState<string>(getInitialLang());

  const changeLanguage = async (lang: string) => {
    const code = (lang || "en").toLowerCase();
    setSelectedLanguage(code);
    try { localStorage.setItem("lang", code); } catch (e) {}
    urlParams.set("lang", code);
    window.history.replaceState({}, "", `${window.location.pathname}?${urlParams.toString()}`);
    if (setLanguageCode) await setLanguageCode(code);
  };

  const overlayClicked = () => {
    if(showTermsOfUse) return setShowTermsOfUse(false);
    if(showDisclaimer) return setShowDisclaimer(false);
    setOpen(false);
  }

  const checkboxChanged = (e: any) => {
    setOpen(e.target.checked);
    setShowTermsOfUse(false);
    setShowDisclaimer(false);
  }

  const shareApp = () => {
    window.ReactNativeWebView?.postMessage('SHARE_APP_URL');
  }

  useEffect(() => {
    const settings = getMobileAppSettings();
    window.ReactNativeWebView?.postMessage(settings.keepScreenAwake ? 'KEEP_AWAKE_ON' : 'KEEP_AWAKE_OFF');
  }, [settings.keepScreenAwake]);

  const showMenu = !showTermsOfUse && !showDisclaimer;

  const setRememberChanges = (rememberChanges: boolean) => {
    dispatch(setSettings({
      ...settings,
      rememberChanges
    }));
  }

  const setESMALeverage = (ESMALeverage: boolean) => {
    dispatch(setSettings({
      ...settings,
      ESMALeverage
    }));
  }

  const setKeepScreenAwake = (keepScreenAwake: boolean) => {
    dispatch(setSettings({
      ...settings,
      keepScreenAwake
    }));
  }

  return (
    <>
      <div className={styles.menuToggle}>
        <input type="checkbox" checked={open} onChange={checkboxChanged} />
        
        <span></span>
        <span className={styles.midSpan}></span>
        <span></span>
        
        {showDisclaimer ? (
          <div className={styles.menu}>
            <Disclaimer />
          </div>
        ) : null}

        {showTermsOfUse ? (
          <div className={styles.menu}>
            <TermsOfUse />
          </div>
        ) : null}

        {showMenu ? (
          <ul className={styles.menu}>
            <li>
              <div className={styles.langRow}>
                <label>{genericTranslate(languageData, "Language")}</label>
                <select value={selectedLanguage} onChange={(e) => changeLanguage(e.target.value)}>
                  {supportedLanguages.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>
            </li>
            <li>
              <ToggleInput 
                onToggle={setRememberChanges} 
                options={[
                  { text: genericTranslate(languageData, "Save My Changes"), value: true },
                  { text: genericTranslate(languageData, "Reset Data"), value: false }
                ]} 
                defaultValue={settings.rememberChanges} />
            </li>
            <li>
              <ToggleInput 
                onToggle={setKeepScreenAwake} 
                options={[{ text: genericTranslate(languageData, "Keep Screen Awake"), value: true }]} 
                defaultValue={settings.keepScreenAwake} />
            </li>
            <li>
              <ToggleInput 
                onToggle={setESMALeverage} 
                options={[{ text: genericTranslate(languageData, "ESMA Leverage Restriction"), value: true }]} 
                defaultValue={settings.ESMALeverage} />
            </li>
            <hr />
            <li onClick={() => setShowTermsOfUse(true)}>{genericTranslate(languageData, "Terms Of Use")}</li>
            <li onClick={() => setShowDisclaimer(true)}>{genericTranslate(languageData, "Disclaimer")}</li>
            <li onClick={shareApp}>{genericTranslate(languageData, "Share The App")}</li>
          </ul>
        ) : null}

        <div className={styles.overlay} onClick={overlayClicked}></div>
      </div>
    </>
  );
}

export default SettingsMenu;